import React from "react";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

const t = (clave, vars) => i18n.t(`userTeams.${clave}`, vars);

/**
 * La consecuencia de lo elegido tiene que leerse de un vistazo: es el dato que
 * el administrador viene a comprobar. En `caption` gris se perdía.
 *
 * Los colores salen de la paleta y no fijos: el panel tiene tema claro y oscuro,
 * y un color escrito a mano solo acierta en uno de los dos.
 */
const useStyles = makeStyles(theme => ({
  aviso: {
    display: "block",
    marginTop: 6,
    fontSize: 15,
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  linea: {
    display: "block",
    marginTop: 6,
    fontSize: 14,
    color: theme.palette.text.secondary
  },
  destacado: { fontWeight: 700, color: theme.palette.primary.main },
  sinSupervisor: {
    fontWeight: 700,
    color: theme.palette.warning?.main ?? theme.palette.error.main
  },
  falta: { display: "block", marginTop: 6, fontSize: 13, fontWeight: 600 }
}));

/**
 * Equipo(s) de un usuario, según el ÁMBITO de su rol (decisión del cliente,
 * 2026-08-19).
 *
 *   empresa  -> administrador: ve toda la empresa, no se le pide equipo
 *   equipo   -> supervisor: UN equipo, y ese es el que supervisa
 *   personal -> usuario: pertenece, y se le dice quién lo supervisa
 *
 * WHY se mira el ámbito y no el nombre del rol: los roles son libres, así que
 * atar la regla al código 'SUPERVISOR' se rompe en cuanto alguien cree «Jefe de
 * turno». D11: no hardcodear nada.
 *
 * Ya no hay desplegable de «rol en este equipo». Era un campo que mentía: la
 * supervisión salía de `UserGroups.supervisorId`, así que elegir «Supervisor»
 * ahí no convertía a nadie en supervisor. Ahora se deduce y no hay dos verdades.
 */
const UserTeamsField = ({ teams, scope, value, onChange, disabled, userId }) => {
  const classes = useStyles();
  // El administrador ve toda su empresa: pedirle un equipo no significa nada.
  if (scope === "empresa") return null;

  const esSupervisor = scope === "equipo";

  // Cardinalidad 1:1 (cliente 2026-08-19, supera D11): TODOS eligen un equipo,
  // no solo el supervisor. Antes el usuario corriente tenia seleccion multiple.
  const seleccionados = value.map(v => v.userGroupId);
  const equipoDe = id => teams.find(e => e.id === id);
  const nombreDe = id => equipoDe(id)?.name ?? `#${id}`;

  /**
   * Un supervisor solo puede tomar equipos LIBRES (o el suyo).
   *
   * Ocultar los ocupados es la regla que pidió el cliente: si ya hay alguien
   * supervisando ese equipo, ni siquiera debe aparecer en la lista. El
   * procedimiento lo vuelve a comprobar — la pantalla nunca es la guarda.
   */
  const disponibles = esSupervisor
    ? teams.filter(e => !e.supervisorId || Number(e.supervisorId) === Number(userId))
    : teams;

  const marcar = ids =>
    onChange(ids.map(id => ({ userGroupId: id, roleId: null })));

  /** ¿Ya está guardado como supervisor de ese equipo, o lo será al guardar? */
  const yaSupervisa = id =>
    Number(equipoDe(id)?.supervisorId) === Number(userId);

  return (
    <Box style={{ marginTop: 8 }}>
      <FormControl variant="outlined" margin="dense" fullWidth required>
        <InputLabel>{esSupervisor ? t("labelOne") : t("label")}</InputLabel>
        <Select
          value={seleccionados[0] ?? ""}
          onChange={e => marcar(e.target.value ? [e.target.value] : [])}
          disabled={disabled}
          label={esSupervisor ? t("labelOne") : t("label")}
        >
          {disponibles.length === 0 ? (
            <MenuItem disabled value="">
              {esSupervisor ? t("noneFree") : t("empty")}
            </MenuItem>
          ) : (
            disponibles.map(equipo => (
              <MenuItem key={equipo.id} value={equipo.id}>
                <ListItemText primary={equipo.name} />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {/* Supervisor: se distingue lo que YA ES de lo que VA A SER. Anunciar en
          futuro algo que ya está guardado hace dudar de si el cambio se aplicó. */}
      {esSupervisor && seleccionados.length > 0 && (
        <span className={classes.aviso}>
          {yaSupervisa(seleccionados[0])
            ? t("isSupervisor")
            : t("willSupervise", { team: nombreDe(seleccionados[0]) })}
        </span>
      )}

      {/* Usuario: una línea por equipo, con el supervisor destacado — es el
          dato que el administrador viene a comprobar. */}
      {!esSupervisor &&
        seleccionados.map(id => {
          const equipo = equipoDe(id);
          return (
            <span key={id} className={classes.linea}>
              <span className={classes.destacado}>{nombreDe(id)}</span>
              {" — "}
              {equipo?.supervisorName ? (
                <>
                  {t("supervisedByLabel")}{" "}
                  <span className={classes.destacado}>{equipo.supervisorName}</span>
                </>
              ) : (
                <span className={classes.sinSupervisor}>{t("noSupervisor")}</span>
              )}
            </span>
          );
        })}

      {seleccionados.length === 0 && (
        <Typography color="error" component="span" className={classes.falta}>
          {t("required")}
        </Typography>
      )}
    </Box>
  );
};

export default UserTeamsField;
